import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useRefreshDesiredOutcomesSidebar } from 'Hooks/useRefreshEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';

const DesiredOutcomeContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const DESIRED_OUTCOME_REFRESH_STARTED = 'desired-outcome-started';
const DESIRED_OUTCOME_REFRESH_SUCCEEDED = 'desired-outcome-succeeded';
const DESIRED_OUTCOME_REFRESH_FAILED = 'desired-outcome-refresh-failed';


const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    // SAC
    desiredOutcomeCollection: null,
    isDesiredOutcomeRefreshing: false,
    desiredOutcomeRefreshError: null,
    // SAC Groups
    desiredOutcomesCollection: null,
    isOptimisticallyUpdating: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        desiredOutcomeCollection: action.desiredOutcomeCollection,
        desiredOutcomesCollection: action.desiredOutcomesCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case DESIRED_OUTCOME_REFRESH_STARTED: {
      return {
        ...state,
        isDesiredOutcomeRefreshing: true,
        desiredOutcomeRefreshError: null,
      };
    }
    case DESIRED_OUTCOME_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isDesiredOutcomeRefreshing: false,
        desiredOutcomeRefreshError: null,
        desiredOutcomeCollection: action.desiredOutcomeCollection,
        desiredOutcomesCollection: action.desiredOutcomesCollection,
      };
    }
    case DESIRED_OUTCOME_REFRESH_FAILED: {
      return {
        ...state,
        isDesiredOutcomeRefreshing: false,
        desiredOutcomeRefreshError: action.desiredOutcomeRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled DesiredOutcomeContext action type: ${action.type}`);
    }
  }
};

const DesiredOutcomeContextProvider = ({
  relationship,
  children,
}) => {
  const { pageContentResource } = usePageContentContext();

  const { displayErrorMessage } = useSnackbarContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshDesiredOutcomeCollection = React.useCallback(async () => {
    const { desiredOutcomeCollection: desiredOutcomeColl } = state;

    if (desiredOutcomeColl === null) {
      return;
    }

    if (desiredOutcomeColl.resources().length() === 0) {
      return;
    }

    const [desiredOutcomeRes] = desiredOutcomeColl;

    try {
      dispatch({ type: DESIRED_OUTCOME_REFRESH_STARTED });
      const coll = await desiredOutcomeRes.link('self').get();

      dispatch({
        type: DESIRED_OUTCOME_REFRESH_SUCCEEDED,
        desiredOutcomeCollection: coll,
      });
    } catch (err) {
      dispatch({ type: DESIRED_OUTCOME_REFRESH_FAILED, desiredOutcomeRefreshError: err });
    }
  }, [state]);

  const loadCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await relationship.link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        desiredOutcomeCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadCollection);

  useRefreshDesiredOutcomesSidebar(async () => {
    await loadCollection();
  }, []);

  // Refresh the groups whenever the page is loading
  React.useEffect(() => {
    refreshDesiredOutcomeCollection().catch(err => {
      displayErrorMessage(err, 'Failed to refresh desired outcomes collection');
    });
  }, [pageContentResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      desiredOutcomeCollection: state.desiredOutcomeCollection,
      desiredOutcomeResource: state.desiredOutcomeCollection?.resources().head(), // collection is null to start
      isDesiredOutcomeRefreshing: state.isDesiredOutcomeRefreshing,
      desiredOutcomeRefreshError: state.desiredOutcomeRefreshError,
      isOptimisticallyUpdating: state.isOptimisticallyUpdating,
      refreshDesiredOutcomeCollection,
    };
  }, [
    state,
    refreshDesiredOutcomeCollection,
  ]);

  return (
    <DesiredOutcomeContext.Provider value={memoizedValue}>
      {children}
    </DesiredOutcomeContext.Provider>
  );
};

// Use DesiredOutcome State Context
const useDesiredOutcomeContext = () => {
  const context = React.useContext(DesiredOutcomeContext);
  if (context === undefined) {
    throw new Error('useDesiredOutcomeContext must be used within a DesiredOutcomeContextProvider');
  }
  return context;
};

DesiredOutcomeContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  relationship: PropTypes.object.isRequired,
};

export {
  DesiredOutcomeContextProvider,
  useDesiredOutcomeContext,
};
